import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSearch,
  faFilter,
  faSyncAlt,
  faEye,
  faAngleLeft,
  faAngleRight,
  faRobot,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { getDprValidationProcessedAiSummaryHistory } from "../../services/dprValidationUploadApi";

const formatDate = (dt) => {
  if (!dt) return "-";
  return new Date(dt).toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const renderMarkdownAsHtml = (text) =>
  String(text || "")
    .replace(/^### (.*$)/gim, "<h3>$1</h3>")
    .replace(/^## (.*$)/gim, "<h2>$1</h2>")
    .replace(/^# (.*$)/gim, "<h1>$1</h1>")
    .replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/gim, "<em>$1</em>")
    .replace(/^- (.*$)/gim, "<li>$1</li>")
    .replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>")
    .replace(/\n{2,}/g, "</p><p>")
    .replace(/\n/g, "<br/>");

const DprUploadValidationAiSummaryHistoryPage = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);

  const [search, setSearch] = useState("");
  const [generatedBy, setGeneratedBy] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    generated_by: "",
    date_from: "",
    date_to: "",
  });

  const [selectedSummary, setSelectedSummary] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDprValidationProcessedAiSummaryHistory({
        page,
        per_page: perPage,
        ...appliedFilters,
      });
      const data = res.data || {};
      setRows(data.data || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      setRows([]);
      toast.error(error?.response?.data?.message || "Failed to load AI summary history.");
    } finally {
      setLoading(false);
    }
  }, [page, perPage, appliedFilters]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const applyFilters = () => {
    setAppliedFilters({
      search: search.trim(),
      generated_by: generatedBy.trim(),
      date_from: dateFrom,
      date_to: dateTo,
    });
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setGeneratedBy("");
    setDateFrom("");
    setDateTo("");
    setAppliedFilters({
      search: "",
      generated_by: "",
      date_from: "",
      date_to: "",
    });
    setPage(1);
  };

  const formatFilters = (filters) => {
    if (!filters || typeof filters !== "object") return "No filter metadata";
    const labels = {
      upload_id: "Upload ID",
      trans_no: "Trans No",
      loa_no: "LOA No",
      validate_status: "Validate Status",
      validated_by_v2: "Validated By",
      status: "Row Status",
      date_from: "Date From",
      date_to: "Date To",
    };

    const lines = Object.keys(labels)
      .filter((key) => String(filters[key] || "").trim() !== "")
      .map((key) => `${labels[key]}: ${filters[key]}`);

    return lines.length ? lines.join(" | ") : "No filter metadata";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="rounded-2xl bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 p-6 text-white shadow-lg">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                (DPR) AI Summary History
              </h1>
              <p className="mt-1 text-sm text-blue-100">
                Review previously generated AI summaries for filtered DPR processed records.
              </p>
            </div>
            <button
              onClick={() => navigate("/dpr-validation-upload-processed")}
              className="inline-flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/30 transition"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Processed Page
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-sky-100 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <FontAwesomeIcon icon={faFilter} />
            Filters
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs text-gray-500">Search</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="upload id / trans no / model"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Generated By</label>
              <input
                type="text"
                value={generatedBy}
                onChange={(e) => setGeneratedBy(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="username or name"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              <button
                onClick={applyFilters}
                className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
              >
                <FontAwesomeIcon icon={faSearch} />
                Search
              </button>
              <button
                onClick={clearFilters}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-500 hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
            <button
              onClick={fetchRows}
              className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600 hover:bg-sky-50"
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading} /> Refresh
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-sky-100 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">Generated At</th>
                  <th className="px-4 py-3 text-left">Generated By</th>
                  <th className="px-4 py-3 text-left">Filters</th>
                  <th className="px-4 py-3 text-right">Rows</th>
                  <th className="px-4 py-3 text-right">Success</th>
                  <th className="px-4 py-3 text-right">Failed</th>
                  <th className="px-4 py-3 text-right">Below Quota</th>
                  <th className="px-4 py-3 text-left">Model</th>
                  <th className="px-4 py-3 text-right">Tokens</th>
                  <th className="px-4 py-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      {Array.from({ length: 10 }).map((__, j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-3 rounded bg-gray-200" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-gray-400">
                      No AI summary history found for the current filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                        {formatDate(row.created_at)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700">
                        {row.generated_by_name || row.generated_by_username || "-"}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-[360px] truncate" title={formatFilters(row.filters)}>
                        {formatFilters(row.filters)}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-gray-700">
                        {(row.filtered_total_rows || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-emerald-700 font-semibold">
                        {(row.filtered_success_count || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-red-700 font-semibold">
                        {(row.filtered_failed_count || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-amber-700 font-semibold">
                        {(row.below_quota_count || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {row.model_used || "-"}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-gray-600">
                        {(row.total_tokens || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setSelectedSummary(row)}
                          className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                        >
                          <FontAwesomeIcon icon={faEye} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && total > 0 && (
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
              <span>
                Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, total)} of {total}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded px-2 py-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </button>
                <span className="px-2 text-xs font-semibold text-gray-600">
                  Page {page} of {Math.max(1, totalPages)}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded px-2 py-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />

      {selectedSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelectedSummary(null)}
          />
          <div className="relative w-full max-w-3xl max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <FontAwesomeIcon icon={faRobot} className="text-white text-lg" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Generated AI Summary</h2>
                  <p className="text-xs text-blue-100">
                    {selectedSummary.model_used || "AI Model"} | {formatDate(selectedSummary.created_at)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedSummary(null)}
                className="p-2 rounded-lg hover:bg-white/20 text-white transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faXmark} className="text-lg" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                <strong>Filters:</strong> {formatFilters(selectedSummary.filters)}
              </div>
              <div
                className="prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-700 prose-strong:text-gray-800 prose-ul:text-gray-700 prose-li:text-gray-700"
                dangerouslySetInnerHTML={{
                  __html: renderMarkdownAsHtml(selectedSummary.summary_text || ""),
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DprUploadValidationAiSummaryHistoryPage;

